// The scene overlay: letterbox bars, the fade and the subtitles, drawn from the scene player's view each frame (styles
// set straight on the elements, as the HUD's values; decision 0046), and a skip button that dispatches `scene/skip`.
// Restyle `.rpg-scene`.
import type { ScenePlayer, SceneView } from "./index";

/** The overlay's default look (a game overrides `.rpg-scene` rules). */
export const SCENE_CSS = `
.rpg-scene{position:fixed;inset:0;pointer-events:none;z-index:25}
.rpg-scene-bar{position:absolute;left:0;right:0;height:12vh;background:#000;transform:scaleY(0)}
.rpg-scene-bar.top{top:0;transform-origin:top}.rpg-scene-bar.bottom{bottom:0;transform-origin:bottom}
.rpg-scene-fade{position:absolute;inset:0;background:#000;opacity:0}
.rpg-scene-sub{position:absolute;left:50%;bottom:14vh;transform:translateX(-50%);max-width:80vw;color:#fff;text-align:center;
  font:18px/1.4 system-ui,sans-serif;text-shadow:0 1px 3px #000}
.rpg-scene-skip{position:absolute;right:16px;top:14px;pointer-events:auto;font:14px system-ui,sans-serif;background:rgba(0,0,0,.5);color:#fff;border:1px solid rgba(255,255,255,.3);border-radius:8px;padding:4px 10px}
.rpg-scene[hidden]{display:none}
`;

/** Mount the overlay; call `update()` each frame. `skipLabel` shows a skip button (absent: none). */
export function mountSceneOverlay(o: { player: ScenePlayer; dispatch(action: string, payload?: unknown): void; skipLabel?: string; doc?: Document; parent?: HTMLElement }) {
  const doc = o.doc ?? document;
  if (!doc.getElementById("rpg-scene-css")) {
    const s = doc.createElement("style");
    s.id = "rpg-scene-css";
    s.textContent = SCENE_CSS;
    doc.head.appendChild(s);
  }
  const root = doc.createElement("div");
  root.className = "rpg-scene";
  root.hidden = true;
  const top = doc.createElement("div");
  top.className = "rpg-scene-bar top";
  const bottom = doc.createElement("div");
  bottom.className = "rpg-scene-bar bottom";
  const fade = doc.createElement("div");
  fade.className = "rpg-scene-fade";
  const sub = doc.createElement("div");
  sub.className = "rpg-scene-sub";
  sub.setAttribute("aria-live", "polite");
  root.append(top, bottom, fade, sub);
  if (o.skipLabel) {
    const b = doc.createElement("button");
    b.className = "rpg-scene-skip";
    b.textContent = o.skipLabel;
    b.addEventListener("click", () => o.dispatch(o.player.actions.skip));
    root.append(b);
  }
  (o.parent ?? doc.body).appendChild(root);
  return {
    el: root,
    /** Draw the view now. */
    update(): SceneView | null {
      const v = o.player.view();
      root.hidden = v === null;
      if (!v) return v;
      top.style.transform = bottom.style.transform = `scaleY(${v.letterbox})`;
      fade.style.opacity = String(v.fade);
      sub.textContent = v.lines.map((l) => l.text).join(" ");
      return v;
    },
    remove(): void {
      root.remove();
    },
  };
}
